import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
  Image,
  Dimensions,
  ScrollView,
  Linking,
  Modal,
} from 'react-native';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import SocialAuthService from '../services/SocialAuthService';
import CheckerPattern from '../components/CheckerPattern';

const { width } = Dimensions.get('window');

// Google icon
const GoogleIcon = ({ color }) => (
  <View style={styles.socialIconContainer}>
    <Text style={[styles.googleIconText, { color }]}>G</Text>
  </View>
);

// Apple icon
const AppleIcon = ({ color }) => (
  <View style={styles.socialIconContainer}>
    <Text style={[styles.appleIconText, { color }]}></Text>
  </View>
);

// Social media icons for follow links
const InstagramIcon = ({ color, size = 18 }) => (
  <View style={[styles.socialMediaIcon, { width: size, height: size }]}>
    <Text style={[styles.socialMediaIconText, { color, fontSize: size }]}>📷</Text>
  </View>
);

const LinkedInIcon = ({ color, size = 18 }) => (
  <View style={[styles.socialMediaIcon, { width: size, height: size }]}>
    <Text style={[styles.socialMediaIconText, { color, fontSize: size - 2, fontWeight: '800' }]}>in</Text>
  </View>
);

const TwitterIcon = ({ color, size = 18 }) => (
  <View style={[styles.socialMediaIcon, { width: size, height: size }]}>
    <Text style={[styles.socialMediaIconText, { color, fontSize: size - 2, fontWeight: '800' }]}>𝕏</Text>
  </View>
);

const TikTokIcon = ({ color, size = 18 }) => (
  <View style={[styles.socialMediaIcon, { width: size, height: size }]}>
    <Text style={[styles.socialMediaIconText, { color, fontSize: size - 2, fontWeight: '800' }]}>♪</Text>
  </View>
);

// Social media links data
const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/chessmaps/',
    Icon: InstagramIcon,
    hoverColor: '#E1306C',
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/chessmaps/about/',
    Icon: LinkedInIcon,
    hoverColor: '#0077B5',
  },
  {
    name: 'Twitter',
    url: 'https://x.com/overtheboardapp',
    Icon: TwitterIcon,
    hoverColor: '#000000',
  },
  {
    name: 'TikTok',
    url: 'https://www.tiktok.com/@chessmapsapp',
    Icon: TikTokIcon,
    hoverColor: '#000000',
  },
];

const LoginScreen = ({ navigation }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [appleAvailable, setAppleAvailable] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { signIn, signInWithApple, signInWithGoogle, loading: authLoading, isSignedIn, user: clerkUser } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { showToast } = useToast();

  // Theme colors
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    card: isDarkMode ? '#141414' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    border: isDarkMode ? '#2A2A2A' : '#E5E5E5',
    inputBg: isDarkMode ? '#1A1A1A' : '#F5F5F5',
    accent: '#94EB98',
    accentDark: '#7BC97F',
  };

  useEffect(() => {
    checkAppleAvailability();
    
    // Display any stored debug logs from before reload
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      const logs = localStorage.getItem('auth_debug_logs');
      if (logs) {
        console.log('📋 Auth Debug Logs from previous session:');
        console.log(JSON.parse(logs));
      }
    }
  }, []);

  // Show toast after redirect from Complete Profile timeout ("Account created. Sign in with Google to continue.")
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
    const message = sessionStorage.getItem('chessmaps_pending_toast');
    if (message) {
      sessionStorage.removeItem('chessmaps_pending_toast');
      showToast(message, 'success');
    }
  }, [showToast]);

  // After completing profile without a DB row we redirect here with a flag.
  // Only start Google sign-in if we don't already have a session (Clerk may restore from cookies after load).
  const reauthAuthRef = React.useRef({ isSignedIn, clerkUser });
  reauthAuthRef.current = { isSignedIn, clerkUser };
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
    if (sessionStorage.getItem('chessmaps_reauth_google') !== '1') return;
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
      body: JSON.stringify({
        sessionId: '81b6b1',
        runId: 'reroute-investigation-1',
        hypothesisId: 'H4',
        location: 'LoginScreen.js:reauth-effect',
        message: 'Reauth effect run',
        data: { authLoading, isSignedIn, hasClerkUser: !!clerkUser, clerkUserId: clerkUser?.id || null },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion agent log
    if (authLoading) return;
    if (isSignedIn && clerkUser) {
      sessionStorage.removeItem('chessmaps_reauth_google');
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
        body: JSON.stringify({
          sessionId: '81b6b1',
          runId: 'reroute-investigation-1',
          hypothesisId: 'H4',
          location: 'LoginScreen.js:reauth-skip-google',
          message: 'Skipping signInWithGoogle — already have session',
          data: { immediate: true },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion agent log
      return;
    }
    // Clerk often reports "loaded" before restoring session from cookies. Wait 900ms, then
    // re-check via ref; only call signInWithGoogle if still no session.
    const timeoutId = setTimeout(() => {
      const { isSignedIn: nowSignedIn, clerkUser: nowUser } = reauthAuthRef.current;
      sessionStorage.removeItem('chessmaps_reauth_google');
      if (nowSignedIn && nowUser) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
          body: JSON.stringify({
            sessionId: '81b6b1',
            runId: 'reroute-investigation-1',
            hypothesisId: 'H4',
            location: 'LoginScreen.js:reauth-skip-google-after-delay',
            message: 'Skipping signInWithGoogle — session appeared after delay',
            data: { delayMs: 900 },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion agent log
        return;
      }
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
        body: JSON.stringify({
          sessionId: '81b6b1',
          runId: 'reroute-investigation-1',
          hypothesisId: 'H4',
          location: 'LoginScreen.js:reauth-call-google',
          message: 'Calling signInWithGoogle after delay',
          data: { delayMs: 900, nowSignedIn: nowSignedIn === true, hasNowUser: !!nowUser },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion agent log
      signInWithGoogle();
    }, 900);
    return () => clearTimeout(timeoutId);
  }, [authLoading, isSignedIn, clerkUser, signInWithGoogle]);

  const checkAppleAvailability = async () => {
    const available = await SocialAuthService.isAppleSignInAvailable();
    setAppleAvailable(available);
  };

  const handleLogin = async () => {
    if (!username || !password) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    setErrorMessage('');
    setLoading(true);
    const result = await signIn(username, password);
    setLoading(false);

    if (!result.success) {
      // Show user-friendly error messages
      let errorText = result.error;
      
      if (result.error?.includes('User does not exist') || result.error?.includes('UserNotFoundException')) {
        errorText = 'Account not found. Please check your username or sign up.';
      } else if (result.error?.includes('Incorrect username or password')) {
        errorText = 'Incorrect username or password. Please try again.';
      } else if (result.error?.includes('User is not confirmed')) {
        errorText = 'Please verify your email before signing in.';
      } else if (result.error?.includes('Network')) {
        errorText = 'Network error. Please check your connection.';
      }
      
      setErrorMessage(errorText);
    }
  };

  const handleAppleSignIn = async () => {
    setErrorMessage('');
    setSocialLoading('apple');
    try {
      const result = await signInWithApple();
      
      // Hosted UI triggers a redirect, so if we get here with redirecting: true,
      // the browser/WebBrowser will handle the flow
      if (result.redirecting) {
        console.log('🔄 Redirecting to Apple Sign-In via Hosted UI...');
        // Don't reset loading - we're redirecting away
        return;
      }
      
      if (!result.success) {
        setErrorMessage(result.error || 'Apple Sign-In failed. Please try again.');
        setSocialLoading(null);
        return;
      }
      
      setSocialLoading(null);
    } catch (error) {
      console.error('Apple sign-in error:', error);
      setErrorMessage(error.message || 'An unexpected error occurred');
      setSocialLoading(null);
    }
  };

  const handleGoogleSignIn = async () => {
    const debugLog = (message, data = null) => {
      console.log(message, data || '');
      // Store in localStorage so we can see it after reload
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        const logs = JSON.parse(localStorage.getItem('auth_debug_logs') || '[]');
        logs.push({ time: new Date().toISOString(), message, data });
        localStorage.setItem('auth_debug_logs', JSON.stringify(logs.slice(-20))); // Keep last 20
      }
    };

    debugLog('🎯 handleGoogleSignIn called');
    setErrorMessage('');
    setSocialLoading('google');
    
    try {
      debugLog('🎯 Calling signInWithGoogle...');
      const result = await signInWithGoogle();
      debugLog('🎯 signInWithGoogle result:', result);
      
      // Hosted UI triggers a redirect, so if we get here with redirecting: true,
      // the browser/WebBrowser will handle the flow
      if (result.redirecting) {
        debugLog('🔄 Redirecting to Google Sign-In via Hosted UI...');
        // Don't reset loading - we're redirecting away
        return;
      }
      
      if (!result.success) {
        let errorText = result.error || 'An error occurred';
        
        if (result.error?.includes('Network')) {
          errorText = 'Network error. Please check your connection.';
        }
        
        debugLog('❌ Google Sign-In failed:', errorText);
        setErrorMessage(errorText);
        setSocialLoading(null);
        return;
      }
      
      setSocialLoading(null);
    } catch (error) {
      debugLog('❌ Google sign-in exception:', error.message);
      setErrorMessage(error.message || 'An unexpected error occurred');
      setSocialLoading(null);
    }
  };


  return (
    <View style={[styles.container, { backgroundColor: theme.bg }]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
      
      {/* Subtle checker pattern background */}
      <CheckerPattern isDarkMode={isDarkMode} opacity={0.008} />
      
      {/* Theme Toggle */}
      <Pressable 
        style={styles.themeToggle} 
        onPress={toggleDarkMode}
        accessibilityRole="button"
        accessibilityLabel="Toggle theme"
      >
        <Text style={[styles.themeToggleText, { color: theme.textSecondary }]}>
          {isDarkMode ? '☀️' : '🌙'}
        </Text>
      </Pressable>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView 
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Logo & Branding */}
          <View style={styles.brandSection}>
            <Image
              source={require('../../assets/chess-maps logo.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <View style={styles.brandNameContainer}>
              <Text style={[styles.brandName, { color: theme.text }]}>
                Chess<Text style={{ color: theme.accent }}>Maps</Text>
              </Text>
              <View style={[styles.betaBadge, { backgroundColor: theme.border }]}>
                <Text style={[styles.betaText, { color: theme.textSecondary }]}>BETA</Text>
              </View>
            </View>
            <Text style={[styles.tagline, { color: theme.textSecondary }]}>
              Find your next game
            </Text>
          </View>

          {/* Login Form */}
          <View style={styles.formSection}>
            {/* Social Login Buttons */}
            {appleAvailable && (
              <View style={styles.socialSection}>
                <Pressable
                  style={({ pressed }) => [
                    styles.socialButton,
                    { 
                      backgroundColor: theme.card,
                      borderColor: theme.border,
                      opacity: pressed ? 0.8 : 1,
                    },
                  ]}
                  onPress={handleAppleSignIn}
                  disabled={socialLoading !== null}
                  accessibilityRole="button"
                  accessibilityLabel="Continue with Apple"
                >
                  {socialLoading === 'apple' ? (
                    <ActivityIndicator size="small" color={theme.text} />
                  ) : (
                    <>
                      <AppleIcon color={theme.text} />
                      <Text style={[styles.socialButtonText, { color: theme.text }]}>
                        Continue with Apple
                      </Text>
                    </>
                  )}
                </Pressable>
              </View>
            )}

            {/* Social Media Links - Prominently placed */}
            <View style={[styles.followSection, { backgroundColor: theme.inputBg, paddingVertical: 20, paddingHorizontal: 24, borderRadius: 16, marginBottom: 24 }]}>
              <Text style={[styles.followText, { color: theme.text, fontSize: 14, fontWeight: '600', marginBottom: 16 }]}>
                Follow us for updates
              </Text>
              <View style={styles.socialLinksContainer}>
                {SOCIAL_LINKS.map((social) => (
                  <Pressable
                    key={social.name}
                    style={({ pressed, hovered }) => [
                      styles.socialLinkButton,
                      { 
                        backgroundColor: pressed || hovered ? `${social.hoverColor}20` : theme.card,
                        borderColor: pressed || hovered ? social.hoverColor : theme.border,
                        borderWidth: pressed || hovered ? 2 : 1.5,
                      },
                    ]}
                    onPress={() => Linking.openURL(social.url)}
                    accessibilityRole="link"
                    accessibilityLabel={`Follow us on ${social.name}`}
                  >
                    <social.Icon 
                      color={theme.text} 
                      size={20} 
                    />
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Continue with Google Button */}
            <View style={styles.socialSection}>
              <Pressable
                style={({ pressed }) => [
                  styles.socialButton,
                  { 
                    backgroundColor: theme.card,
                    borderColor: theme.border,
                    opacity: pressed ? 0.8 : 1,
                  },
                ]}
                onPress={(e) => {
                  console.log('🎯 Google button pressed, event:', e);
                  if (Platform.OS === 'web' && e?.preventDefault) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  handleGoogleSignIn();
                }}
                disabled={socialLoading !== null}
                accessibilityRole="button"
                accessibilityLabel="Continue with Google"
              >
                {socialLoading === 'google' ? (
                  <ActivityIndicator size="small" color={theme.text} />
                ) : (
                  <>
                    <GoogleIcon color="#4285F4" />
                    <Text style={[styles.socialButtonText, { color: theme.text }]}>
                      Continue with Google
                    </Text>
                  </>
                )}
              </Pressable>
            </View>

            {/* Divider */}
            <View style={styles.divider}>
              <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
              <Text style={[styles.dividerText, { color: theme.textSecondary }]}>or</Text>
              <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
            </View>

            {/* Input Fields - Borderless */}
            <View style={styles.inputGroup}>
              <TextInput
                style={[
                  styles.input,
                  { color: theme.text, borderBottomColor: theme.border },
                ]}
                placeholder="Username"
                placeholderTextColor={theme.textSecondary}
                value={username}
                onChangeText={setUsername}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <TextInput
                style={[
                  styles.input,
                  { color: theme.text, borderBottomColor: theme.border },
                ]}
                placeholder="Password"
                placeholderTextColor={theme.textSecondary}
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                autoCapitalize="none"
                onSubmitEditing={handleLogin}
              />
            </View>

            {/* Error Message */}
            {errorMessage ? (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{errorMessage}</Text>
              </View>
            ) : null}

            {/* Forgot Password Link */}
            <Pressable
              onPress={() => navigation.navigate('ForgotPassword')}
              style={styles.forgotPasswordContainer}
              accessibilityRole="button"
              accessibilityLabel="Forgot Password"
            >
              <Text style={[styles.forgotPasswordText, { color: theme.accent }]}>
                Forgot password?
              </Text>
            </Pressable>

            {/* Login Button - Quick Pair Style */}
            <Pressable
              style={({ pressed }) => [
                styles.primaryButton,
                { 
                  opacity: pressed ? 0.9 : (loading ? 0.7 : 1),
                },
              ]}
              onPress={handleLogin}
              disabled={loading || socialLoading !== null}
              accessibilityRole="button"
              accessibilityLabel="Log In"
            >
              {loading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.primaryButtonText}>Log In</Text>
              )}
            </Pressable>

            {/* Sign Up Link */}
            <View style={styles.signupSection}>
              <Text style={[styles.signupText, { color: theme.textSecondary }]}>
                Don't have an account?
              </Text>
              <Pressable
                onPress={() => navigation.navigate('Register')}
                accessibilityRole="button"
                accessibilityLabel="Create Account"
              >
                <Text style={[styles.signupLink, { color: theme.accent }]}>
                  Sign Up
                </Text>
              </Pressable>
            </View>

            {/* View Pricing Link */}
            <View style={styles.pricingSection}>
              <Pressable
                onPress={() => navigation.navigate('Pricing')}
                accessibilityRole="button"
                accessibilityLabel="ChessMaps Pro"
                style={({ pressed }) => [
                  styles.pricingButton,
                  { 
                    borderColor: theme.border,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Text style={[styles.pricingButtonText, { color: theme.accent }]}>
                  ⭐ View Pricing Plans
                </Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  themeToggle: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 10,
  },
  themeToggleText: {
    fontSize: 24,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 60,
  },
  brandSection: {
    alignItems: 'center',
    marginBottom: 48,
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 16,
  },
  brandNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  brandName: {
    fontSize: 32,
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  betaBadge: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
    marginLeft: 4,
  },
  betaText: {
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  tagline: {
    fontSize: 16,
    marginTop: 8,
    fontWeight: '400',
  },
  formSection: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  socialSection: {
    gap: 12,
    marginBottom: 24,
  },
  socialButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  socialIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  googleIconText: {
    fontSize: 18,
    fontWeight: '700',
  },
  appleIconText: {
    fontSize: 20,
    fontWeight: '500',
  },
  socialButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    paddingHorizontal: 16,
    fontSize: 13,
    fontWeight: '500',
  },
  inputGroup: {
    gap: 20,
    marginBottom: 24,
  },
  input: {
    paddingHorizontal: 0,
    paddingVertical: 14,
    fontSize: 17,
    borderWidth: 0,
    borderBottomWidth: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { outline: 'none' }),
  },
  errorContainer: {
    backgroundColor: '#FFE5E5',
    borderLeftWidth: 4,
    borderLeftColor: '#FF4444',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: '#CC0000',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  forgotPasswordContainer: {
    alignSelf: 'flex-end',
    marginBottom: 16,
    marginTop: -8,
  },
  forgotPasswordText: {
    fontSize: 14,
    fontWeight: '500',
  },
  primaryButton: {
    backgroundColor: '#84D288',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    // Quick Pair button style shadow
    elevation: 8,
    shadowColor: '#76AF87',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  signupSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
  },
  signupText: {
    fontSize: 14,
  },
  signupLink: {
    fontSize: 14,
    fontWeight: '600',
  },
  pricingSection: {
    marginTop: 20,
    alignItems: 'center',
  },
  pricingButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 1.5,
    borderStyle: 'dashed',
  },
  pricingButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  // Social media follow links
  followSection: {
    alignItems: 'center',
    gap: 12,
  },
  followText: {
    fontSize: 13,
    fontWeight: '500',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  socialLinksContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
  },
  socialLinkButton: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    // Subtle shadow for depth
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  socialMediaIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialMediaIconText: {
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    gap: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 14,
    marginBottom: 8,
  },
  modalInput: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 16,
    marginTop: 8,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonCancel: {
    borderWidth: 1,
    backgroundColor: 'transparent',
  },
  modalButtonSubmit: {
    backgroundColor: '#84D288',
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalButtonTextSubmit: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

export default LoginScreen;
